import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  DropAnimation,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import { zodResolver } from "@hookform/resolvers/zod"
import { Button } from "design-system/atoms/Button"
import { Card, CardContent, CardHeader, CardTitle } from "design-system/molecules/Card"

import { useNavigate } from "@tanstack/react-router"
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "design-system/atoms/Dialog"
import { Input } from "design-system/atoms/Input"
import { Textarea } from "design-system/atoms/Textarea"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "design-system/molecules/DropdownMenu"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "design-system/molecules/Select"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "design-system/organisms/Form"
import { getErrorTranslations } from "lib/i18n"
import { Pencil } from "lucide-react"
import { useBackofficeCampaignsQuery } from "modules/backoffice/hooks/campaigns/useBackofficeCampaignsQuery"
import { useBackofficeGoalsQuery } from "modules/backoffice/hooks/campaigns/useBackofficeGoalsQuery"
import { useBackofficeCanonicalTopicsGroupedQuery } from "modules/backoffice/hooks/canonicalTopics/useBackofficeCanonicalTopicsGroupedQuery"
import { useCreateCanonicalTopicFromSnapshotMutation } from "modules/backoffice/hooks/canonicalTopics/useCreateCanonicalTopicFromSnapshotMutation"
import { useGetBackofficeClusterSnapshotsQuery } from "modules/backoffice/hooks/canonicalTopics/useGetBackofficeClusterSnapshotsQuery"
import { useRelabelBackofficeCanonicalTopicsMutation } from "modules/backoffice/hooks/canonicalTopics/useRelabelBackofficeCanonicalTopicsMutation"
import { useUpdateBackofficeCanonicalTopicMutation } from "modules/backoffice/hooks/canonicalTopics/useUpdateBackofficeCanonicalTopicMutation"
import { useUpdateClusterSnapshotMutation } from "modules/backoffice/hooks/canonicalTopics/useUpdateBackofficeClusterSnapshotMutation"
import { NEW_CANONICAL_TOPIC_DROP_ZONE_ID } from "modules/backoffice/lib/canonicalTopics/constants"
import { GroupedTreemap } from "modules/dashboard/components/Treemaps/GroupedTreemap"
import { TimeRangeSelector } from "modules/shared/components/TimeRange/TimeRangeSelector"
import { NodePayload } from "modules/dashboard/components/Treemaps/TreemapChart"
import { Loading } from "modules/shared/components/Loading"
import { formatCanonicalTopicLabel } from "modules/shared/lib/formatCanonicalTopicLabel"
import { useCallback, useEffect, useMemo, useState } from "react"
import { useForm } from "react-hook-form"
import { Route as CanonicalTopicsRoute } from "routes/_base/_auth/backoffice/organizations/$id/companies/$companyId/canonical-topics/list"
import {
  CanonicalTopicGroupedByEnum,
  ClusterDomain,
  ClusterDomainEnum,
  ClusterSnapshotDataSchema,
  GoalTypeEnum,
  TimeFrameEnum,
} from "schema"
import { toast } from "sonner"
import { z } from "zod"
import { ClusterSnapshotItem } from "./ClusterSnapshotItem"
import { ClusterSnapshotList } from "./ClusterSnapshotList"
import { NewCanonicalTopicDropZone } from "./NewCanonicalTopicDropZone"

const UpdateTopicSchema = z.object({
  label: z.string().min(1, "Label is required"),
  description: z.string().optional(),
})

type UpdateTopicFormValues = z.infer<typeof UpdateTopicSchema>
type ClusterSnapshot = z.infer<typeof ClusterSnapshotDataSchema>

type Props = {
  companyId: string
  topic: { id: string; label: string; description: string | null }
}

const EditTopicDialog = ({ companyId, topic }: Props) => {
  const [open, setOpen] = useState(false)
  const updateTopicMutation = useUpdateBackofficeCanonicalTopicMutation({
    companyId,
    onSuccess: () => setOpen(false),
  })

  const form = useForm<UpdateTopicFormValues>({
    resolver: zodResolver(UpdateTopicSchema),
    defaultValues: {
      label: topic.label,
      description: topic.description || "",
    },
  })

  const onSubmit = (values: UpdateTopicFormValues) => {
    updateTopicMutation.mutate({
      canonicalTopicId: topic.id,
      label: values.label,
      description: values.description,
    })
  }

  useEffect(() => {
    form.reset({
      label: topic.label,
      description: topic.description || "",
    })
  }, [topic, form])

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="h-8 w-8">
          <Pencil className="h-4 w-4" />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{"Edit Topic"}</DialogTitle>
        </DialogHeader>
        <Form {...form} errorTranslations={getErrorTranslations()}>
          <div className="space-y-4">
            <FormField
              control={form.control}
              name="label"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{"Label"}</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{"Description"}</FormLabel>
                  <FormControl>
                    <Textarea {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type="button"
                onClick={form.handleSubmit(onSubmit)}
                disabled={updateTopicMutation.isPending}
              >
                {updateTopicMutation.isPending ? "Saving..." : "Save"}
              </Button>
            </DialogFooter>
          </div>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

export const BackofficeCanonicalTopics = () => {
  const { id: organizationId, companyId } = CanonicalTopicsRoute.useParams()
  const search = CanonicalTopicsRoute.useSearch()
  const navigate = useNavigate()

  const [selectedTopicIds, setSelectedTopicIds] = useState<string[]>([])
  const selectedCampaignId = search.campaignId || null
  const selectedGoalId = search.goalId || null
  const selectedDomain = search.domain || ClusterDomainEnum.VoiceOfCustomer
  const timeFrame = search.timeFrame || TimeFrameEnum.Last3Months
  const [activeId, setActiveId] = useState<string | null>(null)
  const [optimisticAssignments, setOptimisticAssignments] = useState<Record<string, string>>({})

  const isGoalDomain = selectedDomain === ClusterDomainEnum.Goal
  const shouldLoadCanonicalTopics = !isGoalDomain || (isGoalDomain && selectedGoalId !== null)

  const { data: campaigns, isPending: isCampaignsPending } = useBackofficeCampaignsQuery({
    companyId,
    query: {
      total: 100,
      page: 1,
      sortBy: { field: "name", order: "asc" },
    },
    onSuccess: () => {},
  })

  const { data: goals, isPending: isGoalsPending } = useBackofficeGoalsQuery({
    companyId,
    query: selectedCampaignId
      ? {
          total: 100,
          page: 1,
          filterBy: { campaignId: selectedCampaignId },
        }
      : undefined,
    enabled: !!selectedCampaignId,
  })

  const updateSearch = useCallback(
    (updates: Partial<typeof search>) => {
      navigate({
        to: CanonicalTopicsRoute.fullPath,
        params: { id: organizationId, companyId },
        search: { ...search, ...updates },
      })
    },
    [navigate, organizationId, companyId, search],
  )

  const { data: groupedTopics, isPending: isTopicsPending } =
    useBackofficeCanonicalTopicsGroupedQuery({
      companyId,
      query: {
        domain: selectedDomain,
        goalId: selectedGoalId,
        timeFrame,
      },
      enabled: shouldLoadCanonicalTopics,
    })

  useEffect(() => {
    if (!isGoalDomain) {
      updateSearch({ campaignId: undefined, goalId: undefined })
    }
  }, [isGoalDomain, updateSearch])

  useEffect(() => {
    if (selectedCampaignId === null) {
      updateSearch({ goalId: undefined })
    }
  }, [selectedCampaignId, updateSearch])

  const { data: snapshots1 } = useGetBackofficeClusterSnapshotsQuery({
    companyId,
    canonicalTopicId: selectedTopicIds[0] || "",
  })

  const { data: snapshots2 } = useGetBackofficeClusterSnapshotsQuery({
    companyId,
    canonicalTopicId: selectedTopicIds[1] || "",
  })

  const updateSnapshotMutation = useUpdateClusterSnapshotMutation({
    companyId,
    onSuccess: () => {
      toast.success("Snapshot updated successfully")
    },
  })

  const createCanonicalTopicMutation = useCreateCanonicalTopicFromSnapshotMutation({
    companyId,
    onSuccess: (canonicalTopicId) => {
      toast.success("Canonical topic created successfully")
      setSelectedTopicIds((prev) => {
        if (prev.length >= 2) {
          return [prev[1]!, canonicalTopicId]
        }
        return [...prev, canonicalTopicId]
      })
    },
  })

  const [isRelabelAllConfirmOpen, setIsRelabelAllConfirmOpen] = useState(false)

  const relabelMutation = useRelabelBackofficeCanonicalTopicsMutation({ companyId })

  const allTopicIds = useMemo(
    () => (groupedTopics ?? []).flatMap((g) => g.canonicalTopics).map((t) => t.id),
    [groupedTopics],
  )

  const handleRelabelSelected = () => {
    if (selectedTopicIds.length === 0) return
    relabelMutation.mutate({ canonicalTopicIds: selectedTopicIds })
  }

  const handleConfirmRelabelAll = () => {
    if (allTopicIds.length === 0) return
    relabelMutation.mutate(
      { canonicalTopicIds: allTopicIds },
      { onSettled: () => setIsRelabelAllConfirmOpen(false) },
    )
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  )

  const dropAnimation: DropAnimation = {
    duration: 280,
    easing: "cubic-bezier(0.22, 0.61, 0.36, 1)",
  }

  const allSnapshots = useMemo<ClusterSnapshot[]>(
    () => [...(snapshots1 || []), ...(snapshots2 || [])],
    [snapshots1, snapshots2],
  )

  useEffect(() => {
    if (!allSnapshots.length) return
    setOptimisticAssignments((current) => {
      let changed = false
      const next = { ...current }

      for (const snapshot of allSnapshots) {
        const optimisticTopicId = current[snapshot.id]
        if (optimisticTopicId && snapshot.canonicalTopicId === optimisticTopicId) {
          delete next[snapshot.id]
          changed = true
        }
      }

      return changed ? next : current
    })
  }, [allSnapshots])

  const resolvedSnapshotsByTopic = useMemo(() => {
    const grouped: Record<string, ClusterSnapshot[]> = {}

    for (const snapshot of allSnapshots) {
      const topicId = optimisticAssignments[snapshot.id] || snapshot.canonicalTopicId
      if (!topicId) continue
      const currentSnapshots = grouped[topicId] || []
      grouped[topicId] = [...currentSnapshots, snapshot]
    }

    return grouped
  }, [allSnapshots, optimisticAssignments])

  const getSnapshotsForTopic = (topicId: string) => resolvedSnapshotsByTopic[topicId] || []

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id?.toString() || null)
  }

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    setActiveId(null)

    if (!over) return

    const snapshotId = active.id as string
    const targetId = over.id as string
    const snapshot = active.data.current?.snapshot

    if (!snapshot) return

    if (targetId === NEW_CANONICAL_TOPIC_DROP_ZONE_ID) {
      createCanonicalTopicMutation.mutate(
        {
          clusterSnapshotId: snapshotId,
        },
        {
          onError: () => {
            toast.error("Failed to create canonical topic")
          },
        },
      )
      return
    }

    if (snapshot.canonicalTopicId !== targetId) {
      setOptimisticAssignments((prev) => ({
        ...prev,
        [snapshotId]: targetId,
      }))

      updateSnapshotMutation.mutate(
        {
          clusterSnapshotId: snapshotId,
          canonicalTopicId: targetId,
        },
        {
          onError: () => {
            setOptimisticAssignments((prev) => {
              const next = { ...prev }
              delete next[snapshotId]
              return next
            })
          },
        },
      )
    }
  }

  const getActiveSnapshot = () => {
    if (!activeId) return null
    return allSnapshots.find((s) => s.id === activeId)
  }

  const handleNodeClick = (node: NodePayload) => {
    const topicId = node.id?.toString()
    if (!topicId) return

    setSelectedTopicIds((prev) => {
      if (prev.includes(topicId)) {
        return prev.filter((id) => id !== topicId)
      }
      if (prev.length >= 2) {
        return [prev[1]!, topicId]
      }
      return [...prev, topicId]
    })
  }

  const getTopicById = (id: string) =>
    groupedTopics?.flatMap((g) => g.canonicalTopics).find((t) => t.id === id)

  const handleDomainChange = (domain: ClusterDomain) => {
    if (domain !== ClusterDomainEnum.Goal) {
      updateSearch({ domain, campaignId: undefined, goalId: undefined })
    } else {
      updateSearch({ domain })
    }
  }

  const selectedGoal = useMemo(() => {
    return goals?.find((g) => g.id === selectedGoalId)
  }, [goals, selectedGoalId])

  return (
    <div className="flex flex-col gap-4 h-full">
      <Dialog open={isRelabelAllConfirmOpen} onOpenChange={setIsRelabelAllConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{"Re-label all topics in view?"}</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            {`This will regenerate the label and description of ${allTopicIds.length} canonical topic(s). Cluster snapshots are not affected.`}
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsRelabelAllConfirmOpen(false)}>
              {"Cancel"}
            </Button>
            <Button onClick={handleConfirmRelabelAll} disabled={relabelMutation.isPending}>
              {relabelMutation.isPending ? "Starting..." : "Re-label all"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center shrink-0 gap-2 w-full">
        <div className="flex gap-2">
          <Select
            value={selectedDomain}
            onValueChange={(v) => handleDomainChange(v as ClusterDomain)}
          >
            <SelectTrigger className="w-48">
              <SelectValue placeholder={"Select Domain"} />
            </SelectTrigger>
            <SelectContent>
              {Object.values(ClusterDomainEnum).map((domain) => (
                <SelectItem key={domain} value={domain}>
                  {domain}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {isGoalDomain && (
            <>
              <Select
                value={selectedCampaignId || ""}
                onValueChange={(v) => {
                  updateSearch({ campaignId: v || undefined, goalId: undefined })
                }}
                disabled={isCampaignsPending}
              >
                <SelectTrigger className="w-48">
                  <SelectValue placeholder={"Select Campaign"} />
                </SelectTrigger>
                <SelectContent>
                  {campaigns?.map((campaign) => (
                    <SelectItem key={campaign.id} value={campaign.id}>
                      {campaign.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {selectedCampaignId && (
                <Select
                  value={selectedGoalId || ""}
                  onValueChange={(v) => updateSearch({ goalId: v || undefined })}
                  disabled={isGoalsPending || !selectedCampaignId}
                >
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder={"Select Goal"} />
                  </SelectTrigger>
                  <SelectContent>
                    {goals?.map((goal) => (
                      <SelectItem key={goal.id} value={goal.id}>
                        {goal.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </>
          )}
        </div>
        <div className="flex items-center gap-2">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" disabled={relabelMutation.isPending}>
                {relabelMutation.isPending ? "Re-labeling..." : "Re-label"}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                disabled={selectedTopicIds.length === 0}
                onClick={handleRelabelSelected}
              >
                {`Re-label selected (${selectedTopicIds.length})`}
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={allTopicIds.length === 0}
                onClick={() => setIsRelabelAllConfirmOpen(true)}
              >
                {`Re-label all in view (${allTopicIds.length})`}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <TimeRangeSelector
            value={timeFrame}
            onChange={({ timeFrame }) => updateSearch({ timeFrame })}
          />
        </div>
      </div>
      <div className="flex flex-col min-h-0 gap-4">
        {(() => {
          if (!shouldLoadCanonicalTopics) {
            const message =
              isGoalDomain && !selectedCampaignId
                ? "Select a campaign to view goals"
                : "Select a goal to view canonical topics"
            return (
              <div className="flex items-center justify-center h-full text-muted-foreground border-2 border-dashed rounded">
                {message}
              </div>
            )
          }
          if (isTopicsPending) {
            return (
              <div className="flex items-center justify-center h-full">
                <Loading />
              </div>
            )
          }
          return (
            <GroupedTreemap
              domain={selectedDomain}
              timeFrame={timeFrame}
              groupedBy={{ type: CanonicalTopicGroupedByEnum.company }}
              goalId={selectedGoal?.id}
              tagBySentiment={!selectedGoal || selectedGoal?.type === GoalTypeEnum.Open}
              onNodeClick={handleNodeClick}
              data={groupedTopics}
              isPending={isTopicsPending}
            />
          )
        })()}

        <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
          <div className="flex flex-col gap-4">
            <div className="flex flex-row gap-4 h-[50vh]">
              {[0, 1].map((index) => {
                const topicId = selectedTopicIds[index]
                const topic = topicId ? getTopicById(topicId) : null
                return (
                  <Card key={index} className="flex-1 flex flex-col  border-2 border-dashed">
                    <CardHeader className="pb-2 flex flex-row items-center justify-between space-y-0">
                      <CardTitle className="text-lg">
                        {topic ? formatCanonicalTopicLabel(topic.label) : "Select a topic"}
                      </CardTitle>
                      {topic && <EditTopicDialog companyId={companyId} topic={topic} />}
                    </CardHeader>
                    <CardContent className="flex-1 overflow-y-auto">
                      {topic ? (
                        <ClusterSnapshotList
                          snapshots={getSnapshotsForTopic(topic.id)}
                          topicId={topic.id}
                        />
                      ) : (
                        <div className="flex items-center justify-center h-full text-muted-foreground">
                          {"Select a topic from the treemap"}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )
              })}
            </div>
            <NewCanonicalTopicDropZone />
          </div>
          <DragOverlay dropAnimation={dropAnimation}>
            {activeId && getActiveSnapshot() && (
              <ClusterSnapshotItem snapshot={getActiveSnapshot()!} />
            )}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  )
}
